import './login.scss';

import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete
} from '@react-google-maps/api';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Button, Form } from 'react-bootstrap-v5';
import { useTranslation } from 'react-i18next';

import {
  MAP_LIBRARY,
  MapConfig,
  REACT_APP_MAP_KEY_API,
  Languages
} from 'app/utils/common/constants';

import { Coord } from '../models/AuthModel';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToastDefaultConfig } from 'setup/toast/ToastConfig';
import { useAppDispatch, useAppSelector } from 'setup/redux/Hooks';
import { authActions, selectLanguage } from '../redux/AuthSlice';
import { customerApi } from 'app/modules/customers/redux/CustomerApi';
import { clientTimeZoneOffset } from 'app/utils/common/constants';

type Props = {
  rtl?: boolean,
  defaultCenter?: Coord,
  submitLocation: (value: Coord, token: string) => void,
};

const MapLocationPicker: React.FC<Props> = ({
  rtl,
  defaultCenter,
  submitLocation
}) => {

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLang = useAppSelector(selectLanguage);
  const location = useLocation();

  const params = useParams<{ code: string }>();
  
  // Store code in state to use after URL is cleaned
  const [code] = useState<string | undefined>(params.code);
  const hasPublishedGpsDeniedRef = useRef<boolean>(false);
  const hasRequestedLocationOnInitRef = useRef<boolean>(false);
  const shownErrorRef = useRef<string | null>(null);
  const [isGpsDenied, setIsGpsDenied] = useState<boolean>(false);
  const [isLocationConfirmed, setIsLocationConfirmed] = useState<boolean>(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  
  // Reset flags when code changes (new session)
  useEffect(() => {
    hasPublishedGpsDeniedRef.current = false;
    hasRequestedLocationOnInitRef.current = false;
    shownErrorRef.current = null;
    setIsGpsDenied(false);
    setIsLocationConfirmed(false);
  }, [code]);

  // Hide code from URL after component mounts
  useEffect(() => {
    if (params.code) {
      const newPath = location.pathname.replace(`/${params.code}`, '') || '/map-picker';
      const newUrl = newPath + (location.search ? location.search : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [params.code, location.pathname, location.search]);

  useEffect(() => {
    const lang = searchParams.get('language');
    if (!lang) return;
    const normalized = lang.toLowerCase();
    if ((normalized === Languages.ar || normalized === Languages.en) && normalized !== currentLang) {
      dispatch(authActions.changeLanguage(normalized));
    }
  }, [location.search, currentLang, dispatch, searchParams]);

  // const { isLoaded } = useJsApiLoader({
  //   id: 'google-map-script',
  //   googleMapsApiKey: (REACT_APP_MAP_KEY_API || ""),
  //   libraries: MAP_LIBRARY,
  //   version: 'weekly',
  //   language: 'en',
  // });

  const [map, setMap] = useState<any>(null);

  const [center, setCenter] = useState<Coord>({
    lat: defaultCenter?.lat || MapConfig.Saudi.Lat,
    lng: defaultCenter?.lng || MapConfig.Saudi.Lng,
  });

  const [address, setAddress] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastLocationErrorAtRef = useRef<number>(0);

  const autoCompleteRef = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  
  // Reverse geocoding: get address from Lat/Lng
  const fetchAddress = (lat: number, lng: number) => {
    const GeocoderCtor = window?.google?.maps?.Geocoder;
    if (!GeocoderCtor) return;

    const geocoder = new GeocoderCtor();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        const formatted = results[0].formatted_address;
        setAddress(formatted);
      }
    });
  };

  // When map is idle → sync center + address
  const handleIdle = useCallback(() => {
    if (!map) return;

    const lat = map.getCenter()?.lat();
    const lng = map.getCenter()?.lng();

    if (lat === undefined || lng === undefined) return;

    const newCenter = { lat, lng };
    
    const last = lastFetchRef.current;
    if (last && Math.abs(last.lat - lat) < 1e-6 && Math.abs(last.lng - lng) < 1e-6) {
      return;
    }

    // Only update center state if it changed significantly
    setCenter((prev) => {
      if (Math.abs(prev.lat - lat) < 1e-6 && Math.abs(prev.lng - lng) < 1e-6) {
        return prev;
      }
      return newCenter;
    });

    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
    }
    fetchTimerRef.current = setTimeout(() => {
      lastFetchRef.current = newCenter;
      fetchAddress(newCenter.lat, newCenter.lng);
    }, 500);
  }, [map]);

  //-------------------------------------------------------------------
  const onMapLoad = useCallback((mapInstance: any) => {
    setMap(mapInstance);

    const initial = {
      lat: defaultCenter?.lat || MapConfig.Saudi.Lat,
      lng: defaultCenter?.lng || MapConfig.Saudi.Lng,
    };

    lastFetchRef.current = initial;
    fetchAddress(initial.lat, initial.lng);

    // Fix for iOS Safari - ensure map controls and autocomplete are clickable
    const fixControls = () => {
      // Fix map controls (Satellite/Map buttons)
      const mapControls = document.querySelectorAll('.gm-style-mtc button, .gm-style-cc button, .gm-style div[role="button"]');
      mapControls.forEach((control) => {
        if (control && control instanceof HTMLElement) {
          (control.style as any).webkitTapHighlightColor = 'transparent';
          control.style.touchAction = 'manipulation';
          control.style.cursor = 'pointer';
          control.style.pointerEvents = 'auto';
          (control.style as any).webkitUserSelect = 'none';
          control.style.userSelect = 'none';
          (control.style as any).webkitTouchCallout = 'none';
          
          // Add touch event listeners for iOS - single click
          control.addEventListener('touchstart', (e: TouchEvent) => {
            e.stopPropagation();
            // Trigger click immediately on touchstart for single click
            setTimeout(() => {
              control.click();
            }, 0);
          }, { passive: true });
        }
      });

      // Fix Autocomplete input
      const autocompleteInputs = document.querySelectorAll('.pac-container input, .pac-container');
      autocompleteInputs.forEach((input) => {
        if (input && input instanceof HTMLElement) {
          (input.style as any).webkitTapHighlightColor = 'transparent';
          input.style.touchAction = 'manipulation';
          input.style.pointerEvents = 'auto';
          (input.style as any).webkitUserSelect = 'text';
          input.style.userSelect = 'text';
        }
      });
    };

    // Fix immediately and also after a delay to catch dynamically loaded controls
    fixControls();
    setTimeout(fixControls, 300);
    setTimeout(fixControls, 1000);
  }, [defaultCenter?.lat, defaultCenter?.lng]);

  const onMapUnmount = useCallback(() => {
    setMap(null);
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
  }, []);

  // Current location (HTML5 Geolocation)
  const showLocationError = useCallback((msgKey: string) => {
    const now = Date.now();
    // Avoid double toasts fired almost simultaneously (e.g., permission denied prompt)
    if (now - lastLocationErrorAtRef.current < 1000) return;

    // Prevent showing the other error message if one has already been shown
    if (shownErrorRef.current) {
      if ((shownErrorRef.current === 'MAP_PICKER.GET_LOCATION_FAIL' && msgKey === 'MAP_PICKER.UNABLE_GET_LOCATION') ||
          (shownErrorRef.current === 'MAP_PICKER.UNABLE_GET_LOCATION' && msgKey === 'MAP_PICKER.GET_LOCATION_FAIL')) {
        return;
      }
    }

    lastLocationErrorAtRef.current = now;
    shownErrorRef.current = msgKey;
    toast.error(t(msgKey), ToastDefaultConfig());
  }, [t]);

  const getCurrentLocation = () => {
    // Clear search input
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      setSearchValue('');
    }
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };

        setCenter(coords);
        map?.panTo(coords);
        fetchAddress(coords.lat, coords.lng);
      },
      async (error) => {
        // Check if user denied GPS permission
        if (error.code === error.PERMISSION_DENIED) {
          setIsGpsDenied(true);
          
          if (code && !hasPublishedGpsDeniedRef.current) {
            // Publish message to RabbitMQ via API (only once)
            hasPublishedGpsDeniedRef.current = true;
            try {
              await customerApi.publishGpsDeniedMessage({
                code: error.message,
                token: code,
                clientTimeOffset: clientTimeZoneOffset,
              }).catch(() => {
                // Silently fail if API call fails
              });
            } catch (e) {
              console.error('Failed to publish GPS denied message to RabbitMQ:', e);
            }
          }
          showLocationError('MAP_PICKER.GET_LOCATION_FAIL');
        }
      }
    );
  };

  // Autocomplete pick place
  const onPlaceChanged = () => {
    if (autoCompleteRef.current) {
      const place = autoCompleteRef.current.getPlace();
      if (!place?.geometry) return;

      const loc = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setCenter(loc);
      map?.panTo(loc);
      fetchAddress(loc.lat, loc.lng);
      
      // Update search value
      if (searchInputRef.current) {
        setSearchValue(searchInputRef.current.value);
      }
    }
  };

  // Clear search input
  const clearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      setSearchValue('');
      // Reset to default center if needed
      const initial = {
        lat: defaultCenter?.lat || MapConfig.Saudi.Lat,
        lng: defaultCenter?.lng || MapConfig.Saudi.Lng,
      };
      setCenter(initial);
      map?.panTo(initial);
      fetchAddress(initial.lat, initial.lng);
    }
  };

  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const requestLocationOnInit = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error(t('MAP_PICKER.NOT_SUPPORT_GEOLOCATION'), ToastDefaultConfig())
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };

        setCenter(coords);
        map?.panTo(coords);
        fetchAddress(coords.lat, coords.lng);
        
        // Clear search input on initial location request
        if (searchInputRef.current) {
          searchInputRef.current.value = '';
          setSearchValue('');
        }
      },
      async (error) => {
        // Check if user denied GPS permission
        if (error.code === error.PERMISSION_DENIED) {
          setIsGpsDenied(true);
          
          if (code && !hasPublishedGpsDeniedRef.current) {
            // Publish message to RabbitMQ via API (only once)
            hasPublishedGpsDeniedRef.current = true;
            try {
              await customerApi.publishGpsDeniedMessage({
                code: error.message,
                token: code,
                clientTimeOffset: clientTimeZoneOffset,
              }).catch(() => {
                // Silently fail if API call fails
              });
            } catch (e) {
              console.error('Failed to publish GPS denied message to RabbitMQ:', e);
            }
          }
        }
        if (error.code === error.PERMISSION_DENIED) {
          showLocationError('MAP_PICKER.UNABLE_GET_LOCATION')
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [map, t, showLocationError, code]);

  useEffect(() => {
    // Only request location once on initial mount
    if (!hasRequestedLocationOnInitRef.current) {
      hasRequestedLocationOnInitRef.current = true;
      requestLocationOnInit();
    }
  }, [requestLocationOnInit]);
  //-------------------------------------------------------------------
  const updateLocation = () => {
    if (!code) {
      toast.error(t('MAP_PICKER.INVALID_CODE'), ToastDefaultConfig());
      return;
    }
    setIsLocationConfirmed(true);
    submitLocation(center, code);
  }
  //-------------------------------------------------------------------

  return window.google ? (
    <>
      {/* Search (Autocomplete) */}
      <div className="mb-3 p-2 position-relative">
        <Autocomplete
          onLoad={(ref) => {
            autoCompleteRef.current = ref;
            // Fix autocomplete for iOS Safari single click
            setTimeout(() => {
              const pacContainer = document.querySelector('.pac-container');
              if (pacContainer && pacContainer instanceof HTMLElement) {
                (pacContainer.style as any).webkitTapHighlightColor = 'transparent';
                pacContainer.style.touchAction = 'manipulation';
                pacContainer.style.pointerEvents = 'auto';
                
                const pacItems = pacContainer.querySelectorAll('.pac-item');
                pacItems.forEach((item) => {
                  if (item instanceof HTMLElement) {
                    (item.style as any).webkitTapHighlightColor = 'transparent';
                    item.style.touchAction = 'manipulation';
                    item.style.cursor = 'pointer';
                    item.style.pointerEvents = 'auto';
                    
                    item.addEventListener('touchstart', (e: TouchEvent) => {
                      e.stopPropagation();
                      setTimeout(() => {
                        item.click();
                      }, 0);
                    }, { passive: true });
                  }
                });
              }
            }, 100);
          }}
          onPlaceChanged={isGpsDenied ? () => {} : onPlaceChanged}
        >
          <Form.Control
            ref={(e: any) => {
              if (e) {
                searchInputRef.current = e;
                // Track input value changes
                const handleInput = () => {
                  setSearchValue(e.value || '');
                };
                e.addEventListener('input', handleInput);
                e.addEventListener('change', handleInput);
                // Initial value check
                setSearchValue(e.value || '');
              }
            }}
            placeholder={t('MAP_PICKER.SEARCH_PLACE_PLACEHOLDER')}
            className="w-100"
            disabled={isGpsDenied}
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              pointerEvents: isGpsDenied ? 'none' : 'auto',
              paddingRight: searchValue ? (rtl ? '12px' : '40px') : '12px',
              paddingLeft: searchValue && rtl ? '40px' : '12px',
              opacity: isGpsDenied ? 0.6 : 1,
              cursor: isGpsDenied ? 'not-allowed' : 'text'
            }}
          />
        </Autocomplete>
        {searchValue && (
          <button
            type="button"
            onClick={isGpsDenied ? undefined : clearSearch}
            disabled={isGpsDenied}
            className="btn btn-sm position-absolute"
            style={{
              right: rtl ? 'auto' : '20px',
              left: rtl ? '20px' : 'auto',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              padding: '4px 8px',
              cursor: isGpsDenied ? 'not-allowed' : 'pointer',
              zIndex: 10,
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              opacity: isGpsDenied ? 0.6 : 1,
              pointerEvents: isGpsDenied ? 'none' : 'auto'
            }}
          >
            <i className="fas fa-times" style={{ color: '#6c757d', fontSize: '14px' }}></i>
          </button>
        )}
      </div>

      <div className="position-relative">
        {/* Current location button */}
        <Button
          size="sm"
          variant="primary"
          className={
            "position-absolute m-3" +
            (rtl ? " start-0" : " end-0")
          }
          onClick={isGpsDenied ? undefined : getCurrentLocation}
          onTouchStart={(e) => {
            if (isGpsDenied) {
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            getCurrentLocation();
          }}
          disabled={isGpsDenied}
          style={{
            zIndex: 1000,
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            cursor: isGpsDenied ? 'not-allowed' : 'pointer',
            pointerEvents: isGpsDenied ? 'none' : 'auto',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            WebkitTouchCallout: 'none',
            opacity: isGpsDenied ? 0.6 : 1
          }}
        >
          <i className="fas fa-location-crosshairs"></i> {t('MAP_PICKER.MY_LOCATION')}
        </Button>

        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: '60vh',
            borderRadius: '6px',
            opacity: isGpsDenied ? 0.6 : 1,
            pointerEvents: isGpsDenied ? 'none' : 'auto',
          }}
          center={center}
          zoom={15}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          onIdle={isGpsDenied ? undefined : handleIdle}
          onClick={isGpsDenied ? undefined : (e) => {
            const lat = e?.latLng?.lat();
            const lng = e?.latLng?.lng();
            if (lat !== undefined && lng !== undefined) {
              const loc = {lat, lng};
              setCenter(loc);
              map?.panTo(loc);
              fetchAddress(lat, lng);
            }
          }}
          options={{
            streetViewControl: false,
            mapTypeControl: !isGpsDenied,
            fullscreenControl: false,
            gestureHandling: isGpsDenied ? 'none' : 'cooperative',
            disableDoubleClickZoom: isGpsDenied,
            clickableIcons: !isGpsDenied,
            draggable: !isGpsDenied,
            scrollwheel: !isGpsDenied,
            disableDefaultUI: isGpsDenied,
          }}
        >
          {/* Marker at center with animation */}
          <Marker
            position={center}
            draggable={!isGpsDenied}
            onDragEnd={isGpsDenied ? undefined : (e) => {
              const lat = e?.latLng?.lat();
              const lng = e?.latLng?.lng();
              if (lat !== undefined && lng !== undefined) {
                const loc = {lat, lng};
                setCenter(loc);
                map?.panTo(loc);
                fetchAddress(lat, lng);
              }
            }}
            animation={animate ? window?.google?.maps?.Animation?.DROP : undefined}
          />
        </GoogleMap>
      </div>

      {/* Address display */}
      <div className="mt-2">
        <strong>📍 {t('MAP_PICKER.ADDRESS')}</strong> {address || t('MAP_PICKER.LOADING_ADDRESS')}
      </div>

      <div className="d-flex justify-content-center">
        <button
          type='reset'
          className='btn btn-reset mt-5 btn-update-location'
          onClick={() => updateLocation()}
          disabled={!code || isGpsDenied || isLocationConfirmed}
          style={{
            opacity: (!code || isGpsDenied || isLocationConfirmed) ? 0.6 : 1,
            cursor: (!code || isGpsDenied || isLocationConfirmed) ? 'not-allowed' : 'pointer',
            pointerEvents: (!code || isGpsDenied || isLocationConfirmed) ? 'none' : 'auto'
          }}
        >
          {t('MAP_PICKER.CONFIRM_LOCATION')}
        </button>
      </div>
    </>
  ) : <></>;
};

export default MapLocationPicker;
